// @ts-strict-ignore
/** @jsx jsx */
import * as React from 'react'
import { jsx, Themed, useThemeUI } from 'theme-ui'
import { TruHome } from '@uswitch/spark-icons/tru'
import { Link } from './helpers/react'
import { Palette, usePalette } from '../utils/palette'

import { path } from '../utils/obj'

const lookup = (variant) =>
  variant === 'base' ? 'elements.breadcrumbs.base' : `elements.breadcrumbs.variants.${variant}`

const HomeIcon = ({ variant = 'base' }) => {
  const { textColor } = usePalette()
  const { theme } = useThemeUI()
  const iconColor = path(`${lookup(variant)}.homeIcon.color`.split('.'), theme)

  return (
    <div
      sx={{
        display: 'inline-block',
        width: '1.25em',
        height: '1em',
        variant: `${lookup(variant)}.homeIcon`,
      }}
    >
      <TruHome color={textColor || iconColor} />
    </div>
  )
}

const MobileBreadcrumbs = ({
  crumbs,
  customBackIcon = '<',
  customHomeIcon,
  variant = 'base',
  homePath,
}) => {
  const BackIcon = typeof customBackIcon === 'function' ? customBackIcon : () => customBackIcon

  let href, backTo

  if (crumbs.length) {
    const lastCrumb = crumbs[crumbs.length - 1]
    href = lastCrumb.fields?.path
    backTo = lastCrumb.fields?.displayText
  } else {
    href = homePath
    backTo = customHomeIcon || <HomeIcon variant={variant} />
  }

  return (
    <Palette
      as={Link}
      sx={{
        display: 'inline-block',
        color: 'inherit',
        textDecoration: 'none',
        borderBottom: 0,
        fontSize: 'xxs',
        variant: `${lookup(variant)}.mobileLink`,
      }}
      px={{ color: 'textColor' }}
      href={href}
    >
      <span
        sx={{
          marginRight: 'xxs',
          verticalAlign: customBackIcon === 'function' ? 'middle' : undefined,
        }}
      >
        <BackIcon />
      </span>

      {backTo}
    </Palette>
  )
}

const DesktopBreadcrumbs = ({
  crumbs,
  title,
  customSeparator = '>',
  customHomeIcon,
  variant = 'base',
  homePath,
}) => {
  const liStyling = {
    display: 'inline',
  }

  const anchorStyling = {
    color: 'inherit',
    stroke: 'inherit',
    textDecoration: 'none',
    borderBottom: 0,
    variant: `${lookup(variant)}.a`,
    ':visited': {
      color: 'inherit',
    },
  }

  const Separator = typeof customSeparator === 'function' ? customSeparator : () => customSeparator

  const separatorStyling = {
    display: 'inline-block',
    marginX: 'xs',
    verticalAlign: customSeparator === 'function' ? 'middle' : undefined,
  }

  return (
    <Palette
      as={Themed.ul}
      px={{ color: 'textColor' }}
      sx={{
        listStyleType: 'none',
        paddingLeft: 0,
        fontSize: 'xxs',
        marginY: 0,
        variant: `${lookup(variant)}.main`,
      }}
    >
      <li sx={liStyling}>
        <Link sx={anchorStyling} href={homePath}>
          {customHomeIcon || <HomeIcon variant={variant} />}
        </Link>

        <If condition={crumbs.length || title}>
          <span sx={separatorStyling}>
            <Separator />
          </span>
        </If>
      </li>

      {crumbs.map(({ fields = {} }, i) => (
        <li sx={liStyling} key={i}>
          <Link sx={anchorStyling} href={fields.path}>
            {fields.displayText}
          </Link>

          {(i !== crumbs.length - 1 || title) && (
            <span sx={separatorStyling}>
              <Separator />
            </span>
          )}
        </li>
      ))}

      <If condition={title}>
        <Palette
          as={Themed.li}
          px={{ color: 'textColor' }}
          sx={{ ...liStyling, variant: `${lookup(variant)}.title` }}
        >
          {title}
        </Palette>
      </If>
    </Palette>
  )
}

const Breadcrumbs = ({
  crumbs = [],
  title,
  customSeparator = '>',
  customSeparatorSvg,
  customBackIcon = '<',
  customHomeIcon,
  variant = 'base',
  homePath = '/',
  className,
}) => {
  if (
    (crumbs.length && crumbs[0].fields?.path === homePath) ||
    homePath === 'https://www.confused.com'
  ) {
    crumbs = crumbs.slice(1)
  }

  let customSeparatorFunc

  if (customSeparatorSvg) {
    customSeparatorFunc = () => (
      <img
        style={{ width: '8px', height: '8px' }}
        src={customSeparatorSvg}
        alt='Breadcrumb separator'
      />
    )
  }

  return (
    <div sx={{ variant: `${lookup(variant)}.wrapper` }} className={className}>
      <div sx={{ display: ['block', 'none'] }}>
        <MobileBreadcrumbs
          crumbs={crumbs}
          customBackIcon={customBackIcon}
          customHomeIcon={customHomeIcon}
          variant={variant}
          homePath={homePath}
        />
      </div>
      <div sx={{ display: ['none', 'block'] }}>
        <DesktopBreadcrumbs
          crumbs={crumbs}
          title={title}
          customSeparator={customSeparatorFunc || customSeparator}
          customHomeIcon={customHomeIcon}
          variant={variant}
          homePath={homePath}
        />
      </div>
    </div>
  )
}

export default Breadcrumbs
